import React, { useMemo, useState } from 'react';
import cs from 'classnames';
import { Box, Button, Flex } from 'shared/components/display';

import Skeleton from 'react-loading-skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { BUTTON_VARIANTS } from 'shared/styles/button';

import styles from './styles';
import { FieldProps } from 'formik';
import { isEqual } from 'lodash';

export interface ChatFilterOption<Value = string> {
  label: string;
  value: Value;
  selected: boolean;
  hidden?: boolean;
}

type Props<Value> = {
  showSearch?: boolean;
  loading: boolean;
  allowMultipleSelect?: boolean;
  onSelect?: (option: ChatFilterOption<Value>) => void;
  searchPlaceholder?: string;
};

function ButtonSelectInput<Value>({
  field: { value, name, onChange },
  showSearch,
  loading,
  allowMultipleSelect,
  onSelect,
  searchPlaceholder = 'Search',
}: FieldProps<ChatFilterOption<Value>[]> & Props<Value>) {
  const [search, setSearch] = useState('');
  const { options, hasSelectedOption } = useMemo(() => {
    if (!showSearch)
      return {
        options: value,
        hasSelectedOption: value.some((option) => option.selected),
      };
    return {
      options: value.filter((option) => option.label.toLowerCase().includes(search.toLowerCase())),
      hasSelectedOption: value.some((option) => option.selected),
    };
  }, [value, showSearch, loading, search]);

  const disableOptions = !allowMultipleSelect && hasSelectedOption;

  const updateValue = (newValue: ChatFilterOption<Value>): ChatFilterOption<Value>[] => {
    // so we don't have to do equality checks after the value has been updated
    let valueFound = false;
    return value.map((option) => {
      if (valueFound) return option;
      if (isEqual(option.value, newValue.value)) {
        valueFound = true;
        return newValue;
      }
      return option;
    });
  };

  const handleSelectOption = (value: ChatFilterOption<Value>) => (e) => {
    const updatedValue = updateValue(value);
    onChange({ target: { name, value: updatedValue } });
    onSelect?.(value);
  };

  if (loading) return <Skeleton width="100%" height="35px" count={4} />;

  return (
    <Box css={styles} className="button-selection-input">
      {showSearch && (
        <Box className="button-selection-input__search">
          <FontAwesomeIcon icon={faSearch} className="button-selection-input__search-icon" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="button-selection-input__search-input"
          />
        </Box>
      )}
      <hr className="button-selection-input__divider" />
      <Flex className="button-selection-input__options">
        {options.map((option, i) => {
          return (
            <Button
              key={`${option.label}_${i}`}
              type="button"
              variant={BUTTON_VARIANTS.SECONDARY}
              className={cs('button-selection-input__option', 'endWithEllipsis', {
                'button-selection-input__option--selected': option.selected,
                'button-selection-input__option--hidden': option.hidden,
              })}
              onClick={handleSelectOption({ ...option, selected: !option.selected })}
              disabled={disableOptions && !option.selected}
            >
              {option.label}
            </Button>
          );
        })}
      </Flex>
    </Box>
  );
}

export default ButtonSelectInput;
